// src/services/axios.instance.ts
// Axios instance — withCredentials sends the httpOnly session cookie on every request.
import axios, { type InternalAxiosRequestConfig } from 'axios'
import toast from '@/utils/toast'
import { ENV } from '@/config/env'

const axiosInstance = axios.create({
  baseURL:         ENV.API_URL,
  timeout:         15000,
  withCredentials: true, // CRITICAL: browser sends httpOnly cookie automatically
  headers: {
    'Content-Type': 'application/json',
    'X-Client-Type': 'WEB',
    'ngrok-skip-browser-warning': 'true'
  },
})

// ── Request Interceptor ──────────────────────────────────────────────────────
axiosInstance.interceptors.request.use(
  (config) => {
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type']
    }
    return config
  },
  (error) => Promise.reject(error)
)

// ── Response Interceptor ─────────────────────────────────────────────────────
let isRefreshing = false
let failedQueue: { resolve: () => void; reject: (err: unknown) => void }[] = []

const processQueue = (error: unknown = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve()
    }
  })
  failedQueue = []
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error)) return Promise.reject(error)

    const status = error.response?.status

    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Intercept both 401 (Unauthorized) and 403 (Forbidden - which some backends return for expired sessions)
    if ((status === 401 || status === 403) && originalRequest && !originalRequest._retry) {
      // Avoid infinite loop if the error is from public or auth endpoints
      const isPublicEndpoint = originalRequest.url?.includes('/certificates/verify/') ||
                               originalRequest.url?.includes('/auth/refresh') ||
                               originalRequest.url?.includes('/auth/login') ||
                               originalRequest.url?.includes('/auth/forgot-password')

      if (!isPublicEndpoint) {

        if (isRefreshing) {
          return new Promise<void>((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          })
            .then(() => {
              return axiosInstance(originalRequest)
            })
            .catch((err) => {
              return Promise.reject(err)
            })
        }

        originalRequest._retry = true
        isRefreshing = true

        try {
          // Attempt to rotate session token matrices
          const response = await axiosInstance.post('/auth/refresh')

          if (response.data?.success) {
            // Update Redux state with the rotated user data
            const { store } = await import('@/app/store')
            const { setUser } = await import('@/features/auth/auth.slice')

            store.dispatch(setUser(response.data.data))

            processQueue(null)
            isRefreshing = false

            // Retry original request with new rotated cookie session
            return axiosInstance(originalRequest)
          }
        } catch (refreshError) {
          processQueue(refreshError)
          isRefreshing = false
          console.error('[Dualon Interceptor] Token refresh failed:', refreshError)
        }
      }

      // If it was a 401 (Unauthorized) and token refresh failed/skipped, force logout only on protected pages
      if (status === 401) {
        const { store }     = await import('@/app/store')
        const { clearUser } = await import('@/features/auth/auth.slice')
        store.dispatch(clearUser())
        
        const path = window.location.pathname
        const isPublicRoute =
          path === '/login' ||
          path === '/login/' ||
          path === '/forgot-password' ||
          path === '/forgot-password/' ||
          path.startsWith('/forgot-password') ||
          path === '/unauthorized' ||
          path.startsWith('/verify-certificate') ||
          path.startsWith('/certificates/verify')

        if (!isPublicRoute) {
          window.location.replace('/login')
        }
        return Promise.reject(error)
      }
    }

    // Genuinely Forbidden (status 403 and already retried, or failed/skipped scope)
    if (status === 403) {
      toast.error('You do not have permission to perform this action')
    }

    if (status === 500) {
      toast.error('Something went wrong. Please try again.')
    }

    return Promise.reject(error)
  }
)

export default axiosInstance









// // src/services/axios.instance.ts
// import axios from 'axios'
// import toast from 'react-hot-toast'
// import { ENV } from '@/config/env'

// const axiosInstance = axios.create({
//   baseURL: ENV.API_URL,
//   timeout: 15000,
//   withCredentials: true, // Required for cross-site cookies over ngrok
//   headers: {
//     'Content-Type': 'application/json',
//     // 🔑 REQUIRED FOR NGROK: Bypasses the HTML warning screen so JSON passes cleanly
//     'ngrok-skip-browser-warning': 'true'
//   },
// })

// axiosInstance.interceptors.response.use(
//   (response) => response,
//   async (error: unknown) => {
//     if (!axios.isAxiosError(error)) return Promise.reject(error)

//     const status = error.response?.status

//     if (status === 401) {
//       // Avoid infinite redirect loops if the user is already on the login page
//       if (window.location.pathname !== '/login') {
//         const { store } = await import('@/app/store')
//         const { clearUser } = await import('@/features/auth/auth.slice')
//         store.dispatch(clearUser())
//         window.location.replace('/login')
//       }
//       return Promise.reject(error)
//     }

//     if (status === 403) {
//       toast.error('You do not have permission to perform this action')
//     }

//     if (status === 500) {
//       toast.error('Something went wrong. Please try again.')
//     }

//     return Promise.reject(error)
//   }
// )

// export default axiosInstance